import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import * as Styled from 'shared/_styles/StyledMarkdown.style';
import NameDropdown from 'shiftNotes/components/NameDropdown';
import ProfileLink from 'teamProfiles/components/ProfileLink';
import { ExtractedEntity } from 'shiftNotes/_types';
import { Profile } from 'teamProfiles/_types';
import highlightNamesWithEntities from 'shiftNotes/helpers/nameHighlighting';

interface StyledMarkdownProps {
  content?: string;
  enableNameDropdowns?: boolean;
  entities?: ExtractedEntity[];
  profiles?: Profile[];
  disabled?: boolean;
  onProfileChange?: (startPosition: number, newProfileId: string) => void;
  entityProfileChanges?: Record<number, string>;
  isEditMode?: boolean;
  noteId?: string;
}

const StyledMarkdown = ({
  content,
  enableNameDropdowns = false,
  entities = [],
  profiles = [],
  disabled = false,
  onProfileChange,
  entityProfileChanges,
  isEditMode = false,
  noteId,
}: StyledMarkdownProps): JSX.Element => {
  const processedContent = highlightNamesWithEntities(
    content || '',
    entities,
    enableNameDropdowns
  );

  const spanComponent = ({ className, children, ...props }) => {
    if (enableNameDropdowns && className === 'highlighted-name') {
      const propsRecord = props as Record<string, unknown>;
      const dataName = String(propsRecord['data-name']);
      const profileId = String(propsRecord['data-profile-id']);
      const hasProfile = String(propsRecord['data-has-profile']);
      const startPosition = Number(propsRecord['data-start-position']);
      const entityId = String(propsRecord['data-entity-id'] || '');

      const effectiveProfileId =
        entityProfileChanges?.[startPosition] || profileId || undefined;

      if (dataName && profiles.length > 0) {
        let displayName = dataName;
        if (typeof children === 'string') {
          displayName = children;
        } else if (Array.isArray(children)) {
          displayName = children.join('');
        }

        if (isEditMode) {
          return (
            <NameDropdown
              name={dataName}
              profiles={profiles}
              initialProfileId={effectiveProfileId}
              hasProfile={hasProfile === 'true'}
              disabled={disabled}
              entityStartPosition={startPosition}
              entityExtractionId={entityId || undefined}
              noteId={noteId}
              onProfileChange={onProfileChange}
            />
          );
        }

        if (effectiveProfileId) {
          return (
            <ProfileLink
              name={displayName}
              profileId={effectiveProfileId}
              hasProfile={hasProfile === 'true'}
            />
          );
        }
        return (
          <NameDropdown
            name={dataName}
            profiles={profiles}
            initialProfileId={effectiveProfileId}
            hasProfile={hasProfile === 'true'}
            disabled={disabled}
            entityStartPosition={startPosition}
            entityExtractionId={entityId || undefined}
            noteId={noteId}
            onProfileChange={onProfileChange}
            onSelectProfile={(profile) =>
              onProfileChange?.(startPosition, profile.id)
            }
          />
        );
      }
    }
    return (
      <span className={className} {...props}>
        {children}
      </span>
    );
  };

  return (
    <Styled.MarkdownContainer className="markdown-container">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{ span: spanComponent }}
      >
        {processedContent}
      </ReactMarkdown>
    </Styled.MarkdownContainer>
  );
};
export default StyledMarkdown;
